/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ISchemaExpressionModel } from "@adapptio/lib-blueprint";
import { BEM } from "@adapptio/lib-components";
import { CodeEditor, ICodeEditorProps } from "@adapptio/lib-editor-ui";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { useCallback, useEffect, useState } from "react";
import { ErrorList } from "../../../../ui/components/ErrorList/ErrorList";
import { Layout } from "../../../../ui/components/Layout/Layout";
import { LayoutState } from "../../../../ui/components/Layout/LayoutState";
import { ILayoutConfig, LAYOUT_TYPE } from "../../../../ui/components/Layout/LayoutTypes";
import { ViewModel } from "../../Common/ViewModel";

export const ViewEditorExpressionEditorLayout: ILayoutConfig = {
	root: {
		type: LAYOUT_TYPE.COLUMN,
		id: "layout",
		children: [
			{
				type: LAYOUT_TYPE.ROW,
				id: "main",
				size: "100px",
				children: [
					{
						type: LAYOUT_TYPE.PANEL,
						id: "editor",
						isExpandable: false,
						contentComponent: "editor"
					}
				]
			}
		]
	}
};

/**
 * Component props
 */
export interface IExpressionEditorProps {
	viewModel: ViewModel;
}

/**
 * Main Application Component
 *
 * @param props Component props
 */
export const ExpressionEditor: React.FunctionComponent<IExpressionEditorProps> = ({ viewModel }) => {
	const [ layoutState ] = useState(() => new LayoutState(ViewEditorExpressionEditorLayout));

	const model = viewModel.getExpressionModel();
	const [ , setValue ] = useState("");

	const baseClass = BEM("edt-expression");

	const [ expLastValue, setExpLastValue ] = useState<{ result: unknown }>(
		model?.expLastValue as { result: unknown }
	);

	const resultData = expLastValue?.result;
	const parseErrors = model.expParseErrors;
	let resultString = "";

	try {
		resultString = JSON.stringify(resultData) || "(null)";
	} catch (e) {
		resultString = "(error: " + String(e) + ")";
	}

	useEffect(() => {
		if (model) {
			const handleLastValueChange = () => setExpLastValue(model.expLastValue as { result: unknown });

			onEvent(model.expLastValueChangeEvent, handleLastValueChange);

			return () => {
				offEvent(model.expLastValueChangeEvent, handleLastValueChange);
			};
		}
	}, [ model ]);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			setValue(value);

			model.schema.setValue(model as ISchemaExpressionModel, value, true);
		},
		[ model ]
	);

	const editorProps: Partial<ICodeEditorProps> = {
		value: model.value,
		onChange: handleChange,
		parseErrors
	};

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "editor": {
				return (
					<div className={baseClass.element("editor").toClassName()}>
						<h4 className={baseClass.element("editor-head").toClassName()}>Expression tester</h4>
						<div
							className={baseClass
								.element(
									!resultData && !parseErrors.length ? "wrap-expand" : "wrap-not-expand"
								)
								.toClassName()}
						>
							<CodeEditor
								options={{
									minimap: { enabled: false },
									scrollBeyondLastLine: false
								}}
								singleLine={false}
								language="adapptioExpression"
								shouldFocus={true}
								{...editorProps}
							/>
						</div>
						{resultData && (
							<div className={baseClass.element("result-data").toClassName()}>
								<CodeEditor
									value={resultString}
									language="json"
									parseErrors={parseErrors}
									readOnly={true}
									options={{
										minimap: { enabled: false },
										scrollBeyondLastLine: false,
										scrollBeyondLastColumn: 0,
										automaticLayout: true,
										tabSize: 2,
										lineNumbers: "off",
										lineDecorationsWidth: 0,
										wordWrap: "on",
										readOnly: true,
										renderLineHighlightOnlyWhenFocus: true
									}}
								/>
							</div>
						)}
						{parseErrors && parseErrors.length > 0 ? (
							<div className={baseClass.element("errors").toClassName()}>
								<ErrorList
									errorGroups={[
										{
											documentUri: null,
											parseErrors: parseErrors,
											runtimeErrors: [],
											compileErrors: []
										}
									]}
								/>
							</div>
						) : null}
					</div>
				);
			}
			default: {
				return <p>Error: Undefined panel {componentName}.</p>;
			}
		}
	};

	return (
		<div className={baseClass.toClassName()}>
			<Layout state={layoutState} renderComponent={renderComponent} />
		</div>
	);
};
